import styled, { keyframes } from 'styled-components';

const pulseDot = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(0.8); }
`;

const bounceDown = keyframes`
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(7px); }
`;

export const HeroWrap = styled.section`
  min-height: 100vh;
  background-color: #080808;
  position: relative;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  /* Pull hero up behind the sticky transparent header so the dark
     background fills the full viewport including behind the header */
  margin-top: -72px;
  padding-top: 72px;
  @media (max-width: 768px) { margin-top: -60px; padding-top: 60px; }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(160deg, rgba(8,8,8,.96) 0%, rgba(20,16,10,.9) 52%, rgba(8,8,8,.96) 100%);
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 260px;
    background: linear-gradient(to bottom, transparent 0%, rgba(7,16,31,.8) 100%);
    z-index: 0;
    pointer-events: none;
  }
`;

export const GlowOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;

  &.orb-1 {
    width: 640px;
    height: 640px;
    top: -200px;
    right: -200px;
    background: radial-gradient(ellipse, rgba(196,152,14,.18) 0%, transparent 68%);
  }

  &.orb-2 {
    width: 520px;
    height: 520px;
    bottom: -80px;
    left: -160px;
    background: radial-gradient(ellipse, rgba(196,152,14,.1) 0%, transparent 68%);
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto;
  padding: 20px 20px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(196,152,14,.15);
  border: 1px solid rgba(240,200,118,.28);
  border-radius: 50px;
  padding: 0.38rem 1rem;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #e8d28a;
  margin-bottom: 1.75rem;
`;

export const HeroLogoWrap = styled.div`
  margin-bottom: 1.75rem;

  img {
    height: clamp(140px, 22vw, 275px);
    width: auto;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 0 44px rgba(196,152,14,.6)) drop-shadow(0 12px 28px rgba(0,0,0,.65));
  }
`;

export const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  background: #16a34a;
  border-radius: 50%;
  flex-shrink: 0;
  animation: ${pulseDot} 2s ease-in-out infinite;
`;

export const HeroSub = styled.p`
  color: rgba(205,241,255,.88);
  font-size: clamp(1.05rem, 2.5vw, 1.35rem);
  font-weight: 400;
  line-height: 1.55;
  margin: 0 0 2.5rem;
  max-width: 560px;
`;

export const HeroBtns = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 3.5rem;
`;

export const PrimaryBtn = styled.button`
  background: linear-gradient(#f0c876 0%, #b47806 100%);
  color: #0e0906;
  padding: 15px 38px;
  border: none;
  border-radius: 8px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 16px 36px -10px rgba(139,94,0,.7), 0 0 0 1px rgba(139,94,0,.55) inset, 0 1px 0 rgba(255,255,255,.22) inset;

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }

  @media (max-width: 520px) {
    width: 100%;
    padding: 14px 24px;
  }
`;

export const SecondaryBtn = styled.button`
  background: rgba(255,255,255,.1);
  backdrop-filter: blur(10px);
  color: #fff;
  padding: 15px 38px;
  border: 1px solid rgba(255,255,255,.28);
  border-radius: 8px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,.18);
    border-color: rgba(255,255,255,.48);
  }

  @media (max-width: 520px) {
    width: 100%;
    padding: 14px 24px;
  }
`;

export const TrustRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

export const TrustStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1.5rem;

  .value {
    font-family: 'Poppins', sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    color: #fff;
    white-space: nowrap;
  }

  .label {
    font-size: 0.72rem;
    color: rgba(155,228,255,.72);
    white-space: nowrap;
  }
`;

export const TrustDiv = styled.span`
  width: 1px;
  height: 32px;
  background: rgba(255,255,255,.2);
`;

export const ScrollInd = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  color: rgba(255,255,255,.45);
  font-size: 1.1rem;
  z-index: 1;
  animation: ${bounceDown} 2s ease-in-out infinite;
`;

export const Sec = styled.section`
  width: 100%;
  padding: 5rem 2rem;
  background: ${({ bg }) => bg || 'white'};
`;

export const SecInner = styled.div`
  max-width: 1240px;
  margin: 0 auto;
`;

export const TopLine = styled.p`
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${({ light }) => (light ? '#e8d28a' : '#c4980e')};
  margin: 0 0 0.75rem;
`;

export const SecH2 = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700;
  line-height: 1.2;
  color: ${({ light }) => (light ? '#ffffff' : '#1c2237')};
  margin: 0 0 0.75rem;
`;

export const SecDesc = styled.p`
  color: ${({ light }) => (light ? 'rgba(205,241,255,.82)' : '#555')};
  font-size: 1.05rem;
  line-height: 1.75;
  max-width: 640px;
  margin: 0 0 2.5rem;
`;

export const ActionBtn = styled.button`
  background: linear-gradient(#f0c876 0%, #b47806 100%);
  color: #0e0906;
  padding: 14px 36px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 16px 36px -10px rgba(139,94,0,.7), 0 0 0 1px rgba(139,94,0,.55) inset, 0 1px 0 rgba(255,255,255,.22) inset;

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }
`;

export const DCSection = styled.section`
  padding: 6rem 0;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

export const DCInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const DCTopLine = styled.div`
  color: #f00946;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1.4px;
  line-height: 16px;
  text-transform: uppercase;
  margin-bottom: 16px;
  text-align: center;
`;

export const DCHeading = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  margin-bottom: 1rem;
  text-align: center;
  font-size: 2.5rem;
  line-height: 2.5rem;
  color: #1c2237;

  @media (max-width: 991px) {
    font-size: 2rem;
    line-height: 2rem;
  }
`;

export const DCDesc = styled.p`
  color: #6c757d;
  font-size: 1.1rem;
  letter-spacing: 0.04rem;
  line-height: 1.7;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 2rem;
`;

export const DCSportsNavWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 3rem;
`;

export const DCSportTab = styled.button`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 1.1rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 999px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.38 : 1};
  transition: background 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid ${({ $active }) => $active ? 'rgba(59,130,246,0.65)' : 'rgba(255,255,255,0.12)'};
  background: ${({ $active }) => $active ? '#3b82f6' : 'rgba(255,255,255,0.05)'};
  color: ${({ $active }) => $active ? '#ffffff' : '#94a3b8'};
  box-shadow: ${({ $active }) => $active ? '0 4px 16px rgba(59,130,246,0.3)' : 'none'};

  &:hover:not([disabled]) {
    background: ${({ $active }) => $active ? '#2563eb' : 'rgba(255,255,255,0.1)'};
    color: ${({ $active }) => $active ? '#ffffff' : '#cbd5e1'};
  }

  i { font-size: 0.82rem; }
`;

export const DCCardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: flex-start;
  justify-items: center;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FeatBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.75rem;

  &:last-child {
    margin-bottom: 0;
  }

  .ico {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(196,152,14,.14);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #e8d28a;
    font-size: 1.25rem;
  }

  h5 {
    font-family: 'Poppins', sans-serif;
    font-size: 1.05rem;
    font-weight: 700;
    color: #fff;
    margin: 0 0 0.4rem;
  }

  p {
    color: #e8d28a;
    font-size: 0.93rem;
    line-height: 1.65;
    margin: 0;
  }
`;

export const ConfCard = styled.div`
  background: rgba(14,26,45,.9);
  border: 1px solid rgba(155,228,255,.18);
  border-radius: 20px;
  padding: 1.5rem;

  h6 {
    font-family: 'Poppins', sans-serif;
    font-weight: 700;
    color: #fff;
    margin: 0 0 1rem;
    font-size: 0.95rem;
  }
`;

export const BarTrack = styled.div`
  height: 22px;
  background: rgba(255,255,255,.1);
  border-radius: 50px;
  overflow: hidden;
  margin-bottom: 0.75rem;
`;

export const BarFill = styled.div`
  height: 100%;
  border-radius: 50px;
  background: linear-gradient(90deg, #c4980e 0%, #f0c876 100%);
  width: ${({ $w }) => $w || '0%'};
  display: flex;
  align-items: center;
  padding-left: 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
`;

export const PropsLayout = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 2.5rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Bullets = styled.div`
  margin-bottom: 2rem;

  .b {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.65rem;
    color: #334155;
    font-size: 0.96rem;
  }

  i {
    color: #16a34a;
    font-size: 0.85rem;
  }
`;

export const PropsCard = styled.div`
  background: rgba(14,26,45,.92);
  border: 1px solid rgba(155,228,255,.18);
  border-radius: 20px;
  padding: 1.35rem;
  box-shadow: 0 20px 50px rgba(0,0,0,.45);
`;

export const PCHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  .t {
    color: #fff;
    font-weight: 700;
    font-size: 0.96rem;
  }

  .s {
    color: #e8d28a;
    background: rgba(196,152,14,.18);
    border: 1px solid rgba(240,200,118,.25);
    border-radius: 50px;
    padding: 0.2rem 0.55rem;
    font-size: 0.7rem;
    font-weight: 600;
  }
`;

export const PropItem = styled.div`
  border: 1px solid rgba(155,228,255,.13);
  background: rgba(28,34,55,.6);
  border-radius: 14px;
  padding: 0.8rem 0.85rem;
  margin-bottom: 0.75rem;

  .rh {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.4rem;
  }

  .n {
    color: #fff;
    font-size: 0.86rem;
    font-weight: 600;
  }

  .l {
    color: #e8d28a;
    font-size: 0.78rem;
    font-weight: 600;
  }

  .m {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.4rem;
  }

  .e {
    color: #4ade80;
    font-size: 0.78rem;
    font-weight: 600;
  }

  .c {
    color: rgba(205,241,255,.75);
    font-size: 0.72rem;
  }
`;

export const PBar = styled.div`
  height: 7px;
  background: rgba(255,255,255,.1);
  border-radius: 50px;
  overflow: hidden;

  div {
    height: 100%;
    border-radius: 50px;
    background: linear-gradient(90deg, #b47806, #f0c876);
    width: ${({ $w }) => $w};
  }
`;

export const DocGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const DocCard = styled.div`
  background: rgba(14,26,45,.88);
  border: 1px solid rgba(240,200,118,.2);
  border-radius: 18px;
  padding: 1.1rem;

  h4 {
    margin: 0 0 0.45rem;
    color: #fff;
    font-size: 1.02rem;
  }

  p {
    margin: 0;
    color: #e8d28a;
    font-size: 0.92rem;
    line-height: 1.65;
  }
`;

export const FaqItem = styled.div`
  border-bottom: 1px solid rgba(255,255,255,0.1);

  button {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.2rem 0;
    background: none;
    border: none;
    text-align: left;
    font-size: 1rem;
    font-weight: 600;
    color: #f1f5f9;
    cursor: pointer;
    gap: 1rem;

    &:hover {
      color: #f3d99a;
    }

    i {
      color: #c4980e;
      flex-shrink: 0;
    }
  }

  .ans {
    padding-bottom: 1.2rem;
    color: #cbd5e1;
    line-height: 1.7;
    font-size: 0.97rem;
    display: ${({ $open }) => ($open ? 'block' : 'none')};
  }
`;

export const IconLeading = styled.i`
  margin-right: 0.5rem;
`;

export const CardIcon = styled.i`
  margin-right: 0.45rem;
  color: #f0c876;
`;

export const CenterActions = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

export const ConfNote = styled.p`
  color: #e8d28a;
  font-size: 0.93rem;
  line-height: 1.65;
  margin-top: 1rem;
  margin-bottom: 0;
`;

export const DocActions = styled.div`
  margin-top: 1.4rem;
`;

export const BottomCtaInner = styled(SecInner)`
  text-align: center;
`;

export const BottomTitle = styled(SecH2)`
  max-width: 640px;
  margin: 0 auto 0.75rem;
`;

export const BottomDesc = styled(SecDesc)`
  margin: 0 auto 2rem;
`;
